import {Inject, Injectable, Optional} from '@angular/core';
import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import {environment, CustomEnvironment, setApiOverrides} from '../../environments/environment';
import {Observable} from 'rxjs';
import {catchError, tap,  map } from 'rxjs/operators';
import {HttpErrorHandlerService, HandleError} from './http-error-handling.service';
import { AUTH_SERVICE_TOKEN } from './tokens';


const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': 'my-auth-token'
  })
};

@Injectable()
export class JobsService {
  private handleError: HandleError;

  constructor(
    private http: HttpClient,
    httpErrorHandler: HttpErrorHandlerService,
    @Inject(CustomEnvironment) customEnvironment: any,
    @Optional() @Inject(AUTH_SERVICE_TOKEN) private authService?: any,
  ) {
    setApiOverrides(customEnvironment);
    this.handleError = httpErrorHandler.createHandleError('JobsService');
  }

  getOptions() {
    if (this.authService) {
      httpOptions.headers = httpOptions.headers.set('Authorization', this.authService.authorizationHeader());
    } else {
      console.warn('AuthService is not provided!');
    }
    return httpOptions;
  }

  getTraxJobs(
    identity: number, identityType: string, page: number, pageSize: number, showClosed: boolean, sortDirection: number
  ): Observable<any> {
    return this.http.get<any[]>(
      `${environment.api.jobs}/jobs?sd=${sortDirection}&s=created.when&showClosed=${showClosed}&idt=${identityType}&idv=${identity}&p=${page}&ps=${pageSize}`, httpOptions)
      .pipe(
        map(jobs => {
          jobs.map(job => {
            // determine job status
            let status: string;
            if (job.closed && !job.completed) {
              status = null;
            } else if ( job.completed) {
              status = 'Closed';
            } else if (
              job.assigned &&
              ((job.assigned.user).toLowerCase().indexOf('firstmac.com.au') < 0  ||
              job.assigned.user === 'ontrack' )
            ) {
              status = 'Actionable by Borrower';
            } else {
              status = 'Actionable by Firstmac';
            }

            job.status = status;
            return job;
          });

          return jobs;
        }),
        catchError(this.handleError('getTraxJobs', []))
      );
  }

  getTraxJobTypes(): Observable<any> {
    return this.http.get<any[]>(`${environment.api.types}/list/job-types?sort=text`, httpOptions)
      .pipe(
        catchError(this.handleError('getTraxJobTypes', []))
      );
  }

  createTraxJob(jobType, description, crumbsPartyId): Observable<any> {
    const data = {
      attachments: [],
      chain: null,
      title: '',
      description,
      jobType: jobType,
      identities: {
        crumbsPartyId
      },
      partitions: {},
      priority: null,
      parentId: null
    };
    return this.http.post<any[]>(`${environment.api.jobs}/jobs`, data, httpOptions)
      .pipe(
        catchError(this.handleError('getTraxJobTypes', []))
      );
  }

  getDepartments(query?: any): Observable<any> {
    let params = '';
    if (query) {
      params = Object.keys(query).map((key) => `${key}=${query[key]}`).join('&');
    }
    return this.http.get<any[]>(`${environment.api.jobs}/admin/departments?${params}`, httpOptions)
      .pipe(
        catchError(this.handleError('getDepartments', []))
      );
  }

  getUserMemberships(user: any, department: any, role: any, ps = 20): Observable<any> {
    return this.http.get<any[]>(`${environment.api.jobs}/admin/user-memberships?u=${user}&d=${department}&r=${role}&ps=${ps}`, httpOptions)
      .pipe(
        catchError(this.handleError('getUserMemberships', []))
      );
  }

  listUsers(pageSize): Observable<any> {
    return this.http.get<any[]>(`${environment.api.jobs}/admin/users?ps=${pageSize}`, this.getOptions())
      .pipe(
        catchError(this.handleError('listUsers', []))
      );
  }

  getUser(id: number): Observable<any> {
    return this.http.get(`${environment.api.jobs}/admin/users/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('getUser', []))
      );
  }

  getJobsByIdentityAndJobType(idv, idt, jobType) {
    return this.http.get<any[]>(`${environment.api.jobs}/jobs?sd=-1&s=created.when&idt=${idt}&idv=${idv}&p=0&jobType=${jobType}&showOpen=true&showClosed=true`, httpOptions);
  }
}
